/**
 * Application configuration, read and validated once at startup.
 *
 * Mirrors what the backend does: fail immediately, naming every missing
 * value, instead of crashing later inside whatever code first needed it.
 * A missing VITE_API_BASE_URL otherwise surfaces as "Cannot read properties
 * of undefined" from deep inside the HTTP client.
 *
 * Nothing here is a secret: every VITE_ variable ships inside the bundle
 * and is readable by anyone who opens the site.
 */

interface AppConfig {
  /** Base URL of the API, without a trailing slash. */
  apiBaseUrl: string
  /** Entra ID tenant. Empty until the application is registered. */
  entraTenantId: string
  /** Entra ID application (client) id. Empty until registered. */
  entraClientId: string
  /**
   * Scope requested for the API access token. Defaults to
   * `api://<clientId>/access_as_user`, which is what the backend validates.
   */
  entraApiScope: string
  /** True once both Entra values are present; otherwise the app runs in dev mode without MSAL. */
  isAuthConfigured: boolean
}

function read(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key]
  return typeof value === 'string' ? value.trim() : ''
}

function load(): AppConfig {
  const problems: string[] = []

  const apiBaseUrl = read('VITE_API_BASE_URL').replace(/\/+$/, '')
  if (!apiBaseUrl) {
    problems.push('VITE_API_BASE_URL: falta. Es la dirección de la API, por ejemplo http://localhost:8080')
  } else if (!/^https?:\/\//.test(apiBaseUrl)) {
    problems.push(`VITE_API_BASE_URL: "${apiBaseUrl}" debe incluir el esquema (http:// o https://)`)
  }

  if (problems.length > 0) {
    throw new Error(
      'Configuración incompleta. Copia frontend/.env.example a .env.local y llena:\n  - ' +
        problems.join('\n  - '),
    )
  }

  // Entra ID is not required to start: without it the app runs in dev mode
  // (no MSAL, no sign-in screen) against a backend in `dev-bypass` mode.
  const entraTenantId = read('VITE_ENTRA_TENANT_ID')
  const entraClientId = read('VITE_ENTRA_CLIENT_ID')

  const entraApiScope = read('VITE_ENTRA_API_SCOPE') || `api://${entraClientId}/access_as_user`

  return {
    apiBaseUrl,
    entraTenantId,
    entraClientId,
    entraApiScope,
    isAuthConfigured: entraTenantId !== '' && entraClientId !== '',
  }
}

export const config = load()
